import * as React from 'react'
import { Loader2, Pencil, Plus, Tag, Trash2 } from 'lucide-react'

import {
  useDeleteTagRule, usePreviewTagRule, useSaveTagRule, useTagRuleFields, useTagRules,
  type TagRule,
} from '@/lib/queries'
import type { RuleDocument, RuleGroup } from '@/lib/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/misc'
import { Dialog, DialogHeader, DialogTitle, DrawerContent } from '@/components/ui/dialog'
import { EmptyState, LoadingState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { RuleNodeEditor } from '@/features/rule-builder/RuleTree'
import { useRuleTree } from '@/features/rule-builder/useRuleTree'
import { formatNumber } from '@/lib/utils'

function emptyRoot(): RuleGroup {
  return { type: 'group', op: 'AND', not: false, children: [], id: crypto.randomUUID() }
}

function emptyDoc(tag: string): RuleDocument {
  return {
    schema_version: 1,
    name: tag || '标签规则',
    mode: 'query',
    // candidate_budget 不填。它是「生成器最多枚举多少候选」，标签规则不生成
    // 任何东西，只判定已经存在的主体。填 0 会被解析器当成非法值拒掉，而缺省
    // 时服务端自己会给默认值。
    root: emptyRoot(),
  }
}

/**
 * 标签库：条件命中就赋标签。
 *
 * The conditions are the rule builder's, unchanged, so "重复结构 2×3" decides
 * the same way here as it does in 规则管理. The alternative — a second set of
 * predicates written for tagging — would give the product two answers to "is
 * this ABAB", and the one shown beside a domain would be the untested one.
 */
export function TagRulePanel() {
  const { data, isLoading } = useTagRules()
  const [editing, setEditing] = React.useState<TagRule | 'new' | null>(null)
  const remove = useDeleteTagRule()
  const save = useSaveTagRule()
  const toast = useToast()

  const rules = data?.items ?? []
  const pending = data?.pending ?? 0

  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-medium">标签库</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            用条件规则自动打标签。改动任何一条，整个语义库会重新计算一遍 ——
            手动改过标签的主体不受影响。
          </p>
        </div>
        <Button size="sm" onClick={() => setEditing('new')}>
          <Plus className="mr-1 h-3.5 w-3.5" />新建规则
        </Button>
      </div>

      {pending > 0 && (
        // Named, because a half-recomputed table looks like a broken one, and
        // the natural response to that — edit the rule again — restarts the
        // sweep and makes the wait longer.
        <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-500/[0.06] px-3 py-2 text-xs">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
          <span>
            正在重新计算 {formatNumber(pending)} 个主体的标签，期间列表里新旧标签会并存。
          </span>
        </div>
      )}

      {isLoading ? (
        <LoadingState rows={3} label="正在加载标签规则" />
      ) : rules.length === 0 ? (
        <EmptyState
          title="还没有标签规则"
          description="例如：整个域名 · 重复长度 2 · 精确重复 3 次 → 打上「ABAB」。"
        />
      ) : (
        <div className="divide-y rounded-md border">
          {rules.map((rule) => (
            <div key={rule.id} className="flex items-center gap-3 px-3 py-2.5">
              <Badge variant={rule.enabled ? 'default' : 'muted'} className="shrink-0">
                <Tag className="mr-1 h-3 w-3" />{rule.tag}
              </Badge>
              <div className="min-w-0 flex-1">
                {rule.compile_error ? (
                  // A broken rule with a match count of zero looks like a rule
                  // that simply catches nothing. It is not — it never runs.
                  <div className="truncate text-xs text-destructive">
                    规则无法解析，当前不生效：{rule.compile_error}
                  </div>
                ) : (
                  <div className="truncate text-xs text-muted-foreground">
                    {rule.description || '（无说明）'}
                  </div>
                )}
              </div>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {rule.compile_error ? '—' : `命中 ${formatNumber(rule.match_count)}`}
              </span>
              <Switch
                checked={rule.enabled}
                onCheckedChange={async (enabled) => {
                  try {
                    await save.mutateAsync({ ...rule, enabled })
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : '保存失败')
                  }
                }}
              />
              <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setEditing(rule)}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={async () => {
                  try {
                    await remove.mutateAsync(rule.id)
                    toast.success(`已删除「${rule.tag}」`)
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : '删除失败')
                  }
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TagRuleEditor
          key={editing === 'new' ? 'new' : editing.id}
          rule={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

function TagRuleEditor({ rule, onClose }: { rule: TagRule | null; onClose: () => void }) {
  const { data: fieldData } = useTagRuleFields()
  const fields = React.useMemo(() => fieldData?.fields ?? [], [fieldData])
  const save = useSaveTagRule()
  const preview = usePreviewTagRule()
  const toast = useToast()

  const [tag, setTag] = React.useState(rule?.tag ?? '')
  const [description, setDescription] = React.useState(rule?.description ?? '')
  const [enabled, setEnabled] = React.useState(rule?.enabled ?? true)
  const [doc, setDoc] = React.useState<RuleDocument>(
    () => rule?.rule_json ?? emptyDoc(rule?.tag ?? ''),
  )

  const setRoot = React.useCallback(
    (updater: (prev: RuleGroup) => RuleGroup) =>
      setDoc((prev) => ({ ...prev, root: updater(prev.root) })),
    [],
  )
  const treeOps = useRuleTree(setRoot, fields)

  const ctx = {
    fields,
    errors: {},
    nodeCount: doc.root.children.length + 1,
    ...treeOps,
  }

  const payload = React.useMemo<RuleDocument>(
    () => ({ ...doc, name: tag || '标签规则' }),
    [doc, tag],
  )

  async function runPreview() {
    try {
      await preview.mutateAsync(payload)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '预览失败')
    }
  }

  async function submit() {
    if (!tag.trim()) {
      toast.error('请填写标签名')
      return
    }
    if (doc.root.children.length === 0) {
      toast.error('请至少添加一个条件')
      return
    }
    try {
      await save.mutateAsync({
        id: rule?.id, tag: tag.trim(), description, enabled,
        priority: rule?.priority ?? 0, rule_json: payload,
      })
      toast.success('已保存，语义库正在重新计算')
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败')
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DrawerContent className="sm:max-w-3xl">
        <DialogHeader className="border-b px-5 py-4">
          <DialogTitle className="text-sm">
            {rule ? `编辑标签规则 · ${rule.tag}` : '新建标签规则'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <div className="text-xs font-medium">标签名</div>
              <Input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="如 ABAB" />
            </div>
            <div className="space-y-1">
              <div className="text-xs font-medium">说明</div>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="这条规则想抓什么"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            <span className="text-xs text-muted-foreground">
              启用后才会参与计算；停用会让这个标签从所有主体上消失。
            </span>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-medium">条件</div>
            <RuleNodeEditor
              node={doc.root}
              path="$.root"
              depth={0}
              ctx={ctx}
              index={0}
              siblingCount={1}
            />
          </div>

          <div className="rounded-md border bg-muted/30 p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                保存前先看看会命中什么 —— 一条规则要作用在两万多个主体上。
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={runPreview}
                disabled={preview.isPending || doc.root.children.length === 0}
              >
                {preview.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                预览
              </Button>
            </div>
            {preview.data && (
              <div className="mt-2 space-y-1 text-xs">
                <div className="font-medium">
                  命中 {formatNumber(preview.data.matched)} 个主体
                </div>
                {/* Defaulted, not assumed: an absent list is JSON null, and
                    reading .length off it throws during render, which unmounts
                    the editor and loses the rule being written. */}
                {(preview.data.examples ?? []).length > 0 && (
                  <div className="text-muted-foreground">
                    例如：{(preview.data.examples ?? []).join('、')}
                  </div>
                )}
                {preview.data.matched === 0 && (
                  <div className="text-amber-600">
                    没有命中任何主体。检查条件是否互相冲突，或者是否用了标签判断不了的字段。
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose}>取消</Button>
          <Button size="sm" onClick={submit} disabled={save.isPending}>保存</Button>
        </div>
      </DrawerContent>
    </Dialog>
  )
}
